import { CodeSnippet } from "../general/codeSnippet";
import { CookieInfo, StorageType } from "../../types";
import Switch from "../general/switch";
import { capitalize } from "../../util";
import { CookieType, useCookieConsent } from "./cookieConsentProvider";
import { useCallback } from "react";
import Image from "next/image";
import { cn } from "../pricing/PricingSection";

interface CookieTableProps {
  category: string;
  items: CookieInfo[];
}

const typeToIconProps: Record<StorageType, { title: string; icon: string }> = {
  cookie: {
    title: "Cookie",
    icon: "icon-[mdi--cookie]",
  },
  ls: {
    title: "LocalStorage",
    icon: "icon-[mdi--content-save]",
  },
  indexeddb: {
    title: "IndexedDB",
    icon: "icon-[mdi--storage]",
  },
};

const cookieIcon = (type: StorageType) => {
  const { icon, title } = typeToIconProps[type];

  return <span title={title} className={`${icon} w-[24px] h-[24px]`} />;
};

export function CookieTable({ category, items }: CookieTableProps) {
  return (
    <div key={category} className="mb-8">
      <h3 className="text-xl font-semibold mb-4">{category}</h3>
      <div className="w-full overflow-x-auto">
        <table className="w-full table-fixed min-w-[600px] border-collaps bg-brand-gray800 rounded-lg overflow-hidden">
          <colgroup>
            <col style={{ width: "5%", minWidth: "60px" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "47%" }} />
          </colgroup>
          <thead>
            <tr className="[&>th]:p-5 text-left">
              <th>Type</th>
              <th>Domain</th>
              <th>Name</th>
              <th>Expiry (days)</th>
              <th>Info</th>
            </tr>
          </thead>
          <tbody className="text-left [&>tr:nth-of-type(odd)]:bg-brand-gray200">
            {items.map((item, index) => (
              <tr key={index} className="hover:bg-brand-gray800 [&>td]:py-4 [&>td]:px-5 text-left">
                <td className="w-[5%] max-w-[5%]">
                  <div className="flex items-center justify-center">{cookieIcon(item.type)}</div>
                </td>
                <td className="w-[15%] max-w-[15%]">{item.domain}</td>
                <td className="p-3 font-mono text-sm w-[15%] max-w-[15%]">
                  <CodeSnippet code={item.name} />
                </td>
                <td className="w-[10%] max-w-[10%]">{item.expiry}</td>
                <td className="w-[50%] max-w-[50%]">{item.info || "?"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const CookieDeclaration = ({
  cookiesByCategory,
}: {
  cookiesByCategory: Record<string, CookieInfo[]>;
}) => {
  return Object.entries(cookiesByCategory).map(([category, cookies]) => (
    <div key={category}>
      <CookieTable category={category} items={cookies} />
    </div>
  ));
};

const cookieCategories = ["analytics", "marketing", "preferences"] satisfies CookieType[];

export const CatImage = ({
  imageClass,
  wrapperClass,
  width = 842 / 3.5,
  height = 881 / 3.5,
}: {
  imageClass?: string;
  wrapperClass?: string;
  width?: number;
  height?: number;
}) => {
  return (
    <div className={cn("p-5 scale-x-[-1] ml-[5rem]", wrapperClass)}>
      <Image
        alt="cat market"
        className={imageClass}
        // original height and width
        width={width}
        height={height}
        src="/images/cookies/cat_cookie.png"
      />
    </div>
  );
};

const CookieSwitch = ({
  cookieName,
  disabled,
  save,
  value,
}: {
  cookieName: string;
  disabled?: boolean;
  save?: (value: boolean) => void;
  value: boolean;
}) => {
  return (
    <div className="w-[100%] flex justify-center mb-[2rem] [&:last-of-type]:m-0 justify-center [&>div]:w-[50%] mr-auto">
      <div>
        <Switch
          value={value}
          disabled={disabled}
          defaultValue={false}
          id={`${cookieName}-toggle`}
          onToggle={save}
        />
      </div>
      <div className="font-semibold">{capitalize(cookieName)}</div>
    </div>
  );
};

const PreferencesPanel = () => {
  const { savePrefs, preferences } = useCookieConsent();

  const save = useCallback(
    (key: CookieType) => (value: boolean) => {
      savePrefs({ [key]: value });
    },
    [savePrefs],
  );

  return (
    <div className="flex flex-col items-center my-auto">
      <div className="flex items-center flex-col bg-brand-gray200 rounded-lg py-[2rem] px-[3rem]">
        <CookieSwitch value={true} disabled={true} cookieName="functional" />
        {cookieCategories.map((category) => {
          return (
            <CookieSwitch
              key={category}
              value={preferences[category]}
              save={save(category)}
              cookieName={category}
            />
          );
        })}
      </div>
    </div>
  );
};

export const CookiePreferences = () => {
  return (
    <div className="flex flex-col items-center content-center">
      <h1 className="text-lg3 mb-[3rem]">Cookie preferences</h1>
      <div className="flex flex-row">
        <PreferencesPanel />
        <CatImage />
      </div>
    </div>
  );
};
